import * as providers from "../color/provider"
import { type INumericValueColor, type ValuedColorsProvider } from "../color/provider"
import { NutrientType } from "../generated/enums"
import { isString, isUndefined } from "../utils/utils"

export interface INutrientColors {
    baseColor: number[]
    gradient: INumericValueColor[]
}

/**Gradient colours defined for nutrients, based on product rate values */
export const nutrientColors: { [key: string]: INutrientColors } = {
    N: {
        baseColor: [35, 72, 255],

        gradient: [
            { Value: 0, Colour: [108, 182, 255] },
            { Value: 41, Colour: [90, 159, 255] },
            { Value: 81, Colour: [72, 133, 255] },
            { Value: 121, Colour: [54, 104, 255] },
            { Value: 161, Colour: [35, 72, 255] },
            { Value: 201, Colour: [17, 37, 255] },
            { Value: 241, Colour: [0, 0, 255] },
        ],
    },

    P2O5: {
        baseColor: [30, 148, 35],

        gradient: [
            { Value: 0, Colour: [106, 255, 118] },
            { Value: 41, Colour: [76, 219, 85] },
            { Value: 81, Colour: [51, 184, 58] },
            { Value: 121, Colour: [30, 148, 35] },
            { Value: 161, Colour: [15, 113, 18] },
            { Value: 201, Colour: [5, 78, 6] },
            { Value: 241, Colour: [0, 43, 0] },
        ],
    },

    K2O: {
        baseColor: [204, 88, 54],

        gradient: [
            { Value: 0, Colour: [250, 181, 160] },
            { Value: 41, Colour: [238, 155, 130] },
            { Value: 81, Colour: [227, 131, 103] },
            { Value: 121, Colour: [215, 109, 77] },
            { Value: 161, Colour: [204, 88, 54] },
            { Value: 201, Colour: [192, 69, 33] },
            { Value: 241, Colour: [181, 52, 15] },
        ],
    },

    MgO: {
        baseColor: [39, 148, 157],

        gradient: [
            { Value: 0, Colour: [117, 246, 246] },
            { Value: 41, Colour: [86, 213, 217] },
            { Value: 81, Colour: [60, 180, 187] },
            { Value: 121, Colour: [39, 148, 157] },
            { Value: 161, Colour: [22, 117, 127] },
            { Value: 201, Colour: [10, 86, 97] },
            { Value: 241, Colour: [2, 58, 68] },
        ],
    },

    CaO: {
        baseColor: [125, 39, 154],

        gradient: [
            { Value: 0, Colour: [255, 164, 72] },
            { Value: 41, Colour: [221, 137, 52] },
            { Value: 81, Colour: [188, 112, 37] },
            { Value: 121, Colour: [155, 89, 21] },
            { Value: 161, Colour: [121, 67, 11] },
            { Value: 201, Colour: [88, 46, 4] },
            { Value: 241, Colour: [55, 28, 0] },
        ],
    },

    SO3: {
        baseColor: [238, 229, 103],

        gradient: [
            { Value: 0, Colour: [245, 235, 193] },
            { Value: 41, Colour: [251, 236, 182] },
            { Value: 81, Colour: [254, 231, 152] },
            { Value: 121, Colour: [255, 228, 131] },
            { Value: 161, Colour: [254, 216, 77] },
            { Value: 201, Colour: [253, 208, 56] },
            { Value: 241, Colour: [249, 192, 0] },
        ],
    },

    Lime: {
        baseColor: [125, 39, 154],

        gradient: [
            { Value: 0, Colour: [192, 130, 255] },
            { Value: 500, Colour: [167, 94, 221] },
            { Value: 1000, Colour: [146, 63, 187] },
            { Value: 2000, Colour: [125, 39, 154] },
            { Value: 3000, Colour: [103, 20, 120] },
            { Value: 4000, Colour: [80, 7, 86] },
            { Value: 5000, Colour: [53, 0, 53] },
            // Note: the maximum value should actually be 25 in this case,
            // so the last colour is allocated 2 times
            { Value: 25000, Colour: [53, 0, 53] },
        ],
    },
}

const nutrientColorProviders: { [key: string]: ValuedColorsProvider } = {}

export function getNutrientColors(nutrient: NutrientType) {
    return nutrientColors[NutrientType[nutrient]]
}

export function getNutrientBaseColor(nutrient: NutrientType | string) {
    const key = isString(nutrient) ? nutrient : NutrientType[nutrient]

    return nutrientColors[key]?.baseColor
}

export function getNutrientColorProvider(key: string, dynamicScale?: boolean): ValuedColorsProvider {
    let providerKey = key + (dynamicScale ? "/d" : "/s")

    let provider = nutrientColorProviders[providerKey]

    if (isUndefined(provider)) {
        let c = nutrientColors[key]

        if (c && c.gradient.length > 0) {
            provider = providers.createValuedColorsProvider(c.gradient, c.baseColor, dynamicScale)

            nutrientColorProviders[providerKey] = provider
        }
    }

    return provider
}

export function getHorizontalBarBackgroundStyle(key: string, value: number): any {
    var provider = getNutrientColorProvider(key)

    if (provider) {
        return provider.getHorizontalBarBackgroundStyle(value, null, null)
    }
}

export function getNutrientHorizontalBarBackgroundStyle(nutrient: string, value: number, max: number) {
    let colors = nutrientColors[nutrient]
    if (colors) {
        return providers.getHorizontalBarBackgroundStyle(value, 0, max, colors.baseColor)
    }
}
